if (!customElements.get('wls-s-ingredients-drawer')) {
  class WLS_S_IngredientsDrawer extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        lockScroll: 'wls-s-lock-scroll',
        trigger: 'js-ingredients-drawer-trigger',
        wrapper: 'js-ingredients-drawer-wrapper',
        overlay: 'js-ingredients-drawer-overlay',
        close: 'js-ingredients-drawer-close',
      };

      this.selectors = {
        trigger: this.querySelector(`.${this.classes.trigger}`),
        wrapper: this.querySelector(`.${this.classes.wrapper}`),
        overlay: this.querySelector(`.${this.classes.overlay}`),
        close: this.querySelector(`.${this.classes.close}`),
      };

      this.boundHandleTriggerClick = this.handleTriggerClick.bind(this);
      this.boundHandleKeydown = this.handleKeydown.bind(this);
      this.boundHandleOverlayClick = this.handleOverlayClick.bind(this);
    }

    connectedCallback() {
      this.bindEvents();
    }

    disconnectedCallback() {
      this.unbindEvents();
    }

    bindEvents() {
      // Bind trigger click events
      if (this.selectors.trigger) {
        this.selectors.trigger.addEventListener('click', this.boundHandleTriggerClick);
        // Add keyboard support for trigger
        this.selectors.trigger.addEventListener('keydown', (e) => {
          if (e.code === 'Enter' || e.code === 'Space') {
            e.preventDefault();
            this.boundHandleTriggerClick(e);
          }
        });
      }

      // Bind close button click events
      if (this.selectors.close) {
        this.selectors.close.addEventListener('click', this.boundHandleTriggerClick);
      }

      // Bind overlay click events
      if (this.selectors.overlay) {
        this.selectors.overlay.addEventListener('click', this.boundHandleOverlayClick);
      }

      // Bind keyboard events
      document.addEventListener('keydown', this.boundHandleKeydown);
    }

    unbindEvents() {
      if (this.selectors.trigger) {
        this.selectors.trigger.removeEventListener('click', this.boundHandleTriggerClick);
      }

      if (this.selectors.close) {
        this.selectors.close.removeEventListener('click', this.boundHandleTriggerClick);
      }

      if (this.selectors.overlay) {
        this.selectors.overlay.removeEventListener('click', this.boundHandleOverlayClick);
      }

      document.removeEventListener('keydown', this.boundHandleKeydown);
    }

    handleTriggerClick(e) {
      e.preventDefault();
      this.toggle();
    }

    handleOverlayClick(e) {
      e.preventDefault();
      this.close();
    }

    handleKeydown(e) {
      if (e.code === 'Escape' && this.isOpen()) {
        e.preventDefault();
        this.close();
      }
    }

    toggle() {
      if (this.isOpen()) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      if (this.isOpen()) return;

      this.selectors.wrapper.classList.add(this.classes.active);
      this.lockScroll();
      this.setActiveElement(document.activeElement);

      // Set ARIA attributes
      this.setAttribute('aria-hidden', 'false');
      if (this.selectors.trigger) {
        this.selectors.trigger.setAttribute('aria-expanded', 'true');
      }

      // Focus management
      setTimeout(() => {
        const focusElement = this.selectors.close || this.selectors.wrapper;
        if (focusElement) {
          focusElement.focus();
        }
      }, 100);

      // Trigger custom event
      this.dispatchEvent(
        new CustomEvent('wls-s:ingredients-drawer:opened', {
          bubbles: true,
          detail: { drawer: this },
        })
      );
    }

    close() {
      if (!this.isOpen()) return;

      this.selectors.wrapper.classList.remove(this.classes.active);
      this.unlockScroll();

      // Set ARIA attributes
      this.setAttribute('aria-hidden', 'true');
      if (this.selectors.trigger) {
        this.selectors.trigger.setAttribute('aria-expanded', 'false');
      }

      // Restore focus
      if (this.activeElement) {
        this.activeElement.focus();
        this.activeElement = null;
      }

      // Trigger custom event
      this.dispatchEvent(
        new CustomEvent('wls-s:ingredients-drawer:closed', {
          bubbles: true,
          detail: { drawer: this },
        })
      );
    }

    isOpen() {
      return this.selectors.wrapper.classList.contains(this.classes.active);
    }

    lockScroll() {
      document.body.classList.add(this.classes.lockScroll);
    }

    unlockScroll() {
      document.body.classList.remove(this.classes.lockScroll);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    // Public API methods
    openDrawer() {
      this.open();
    }

    closeDrawer() {
      this.close();
    }

    toggleDrawer() {
      this.toggle();
    }

    // Method to check if drawer is open (public API)
    isDrawerOpen() {
      return this.isOpen();
    }
  }

  customElements.define('wls-s-ingredients-drawer', WLS_S_IngredientsDrawer);
}
